import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForRadioButtons,
  saveCurrentValueForRadioButtons,
  toggleRadioButtonWithWebRequest,
} from './radioButtonUtils'
import { STRAVA_AUTOMATION_STAGE_STATUSES } from './stravaAutomationStageStatuses'
import * as stravaUrls from './stravaUrls'
import { StravaSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

const sectionTitle = 'Where You Appear'

const MENU_TITLES = {
  FLYBY: 'Flyby',
  LOCAL_LEGENDS: 'Local Legends',
}

const FLYBY_SETTINGS = {
  NO_ONE: 'No One',
  EVERYONE: 'Everyone',
}

const LOCAL_LEGENDS_SETTINGS = {
  NO_ONE: 'No One',
  EVERYONE: 'Everyone',
}

const FLYBY_WEB_REQUEST_PAYLOAD = {
  [FLYBY_SETTINGS.NO_ONE]: { flyby_visibility: 'no_one' },
  [FLYBY_SETTINGS.EVERYONE]: { flyby_visibility: 'everyone' },
}

const LOCAL_LEGENDS_WEB_REQUEST_PAYLOAD = {
  [LOCAL_LEGENDS_SETTINGS.NO_ONE]: { local_legend_visibility: 'no_one' },
  [LOCAL_LEGENDS_SETTINGS.EVERYONE]: { local_legend_visibility: 'everyone' },
}

// Settings properties
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === 'No One')

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_FLYBY_VISIBILITY = 'GATHER_SETTING_FLYBY_VISIBILITY',
  UPDATE_SETTING_FLYBY_VISIBILITY = 'UPDATE_SETTING_FLYBY_VISIBILITY',
  UNDO_SETTING_FLYBY_VISIBILITY = 'UNDO_SETTING_FLYBY_VISIBILITY',
  GATHER_SETTING_LOCAL_LEGENDS_VISIBILITY = 'GATHER_SETTING_LOCAL_LEGENDS_VISIBILITY',
  UPDATE_SETTING_LOCAL_LEGENDS_VISIBILITY = 'UPDATE_SETTING_LOCAL_LEGENDS_VISIBILITY',
  UNDO_SETTING_LOCAL_LEGENDS_VISIBILITY = 'UNDO_SETTING_LOCAL_LEGENDS_VISIBILITY',
  UPDATE_SETTING_COMMUNITY_FEATURES_FINALIZE = 'UPDATE_SETTING_COMMUNITY_FEATURES_FINALIZE',
  UNDO_SETTING_COMMUNITY_FEATURES_FINALIZE = 'UNDO_SETTING_COMMUNITY_FEATURES_FINALIZE',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_FLYBY_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        FLYBY_SETTINGS,
        StravaSettingKey.FLYBY_VISIBILITY,
        enums.StravaRecommendationKey.STRAVA_COMMUNITY_FEATURES,
        sectionTitle,
        MENU_TITLES.FLYBY,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_LOCAL_LEGENDS_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        LOCAL_LEGENDS_SETTINGS,
        StravaSettingKey.LOCAL_LEGENDS_VISIBILITY,
        enums.StravaRecommendationKey.STRAVA_COMMUNITY_FEATURES,
        sectionTitle,
        MENU_TITLES.LOCAL_LEGENDS,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_FLYBY_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry, userId } = userContext

      // Skip if not selected
      if (
        !settingsSelected ||
        !settingsSelected[StravaSettingKey.FLYBY_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.FLYBY,
        FLYBY_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (!isDry && currentValue !== FLYBY_SETTINGS.NO_ONE) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete: FLYBY_WEB_REQUEST_PAYLOAD[FLYBY_SETTINGS.NO_ONE],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_LOCAL_LEGENDS_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry, userId } = userContext

      // Skip if not selected
      if (
        !settingsSelected ||
        !settingsSelected[StravaSettingKey.LOCAL_LEGENDS_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.LOCAL_LEGENDS,
        LOCAL_LEGENDS_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (!isDry && currentValue !== LOCAL_LEGENDS_SETTINGS.NO_ONE) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            LOCAL_LEGENDS_WEB_REQUEST_PAYLOAD[LOCAL_LEGENDS_SETTINGS.NO_ONE],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_COMMUNITY_FEATURES_FINALIZE,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_FLYBY_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, userId } = userContext
      // Skip if not selected
      if (!initialValues || !initialValues[StravaSettingKey.FLYBY_VISIBILITY]) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.FLYBY,
        FLYBY_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        !isDry &&
        currentValue !== initialValues[StravaSettingKey.FLYBY_VISIBILITY]
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            FLYBY_WEB_REQUEST_PAYLOAD[
              initialValues[StravaSettingKey.FLYBY_VISIBILITY]
            ],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_LOCAL_LEGENDS_VISIBILITY,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, userId } = userContext

      // Skip if not selected
      if (
        !initialValues ||
        !initialValues[StravaSettingKey.LOCAL_LEGENDS_VISIBILITY]
      ) {
        return null
      }

      const currentValue = await getCurrentValueForRadioButtons(
        sectionTitle,
        MENU_TITLES.LOCAL_LEGENDS,
        LOCAL_LEGENDS_SETTINGS,
      )

      // Toggle setting if incorrect one is selected
      if (
        !isDry &&
        currentValue !==
          initialValues[StravaSettingKey.LOCAL_LEGENDS_VISIBILITY]
      ) {
        await toggleRadioButtonWithWebRequest(userId, {
          athlete:
            LOCAL_LEGENDS_WEB_REQUEST_PAYLOAD[
              initialValues[StravaSettingKey.LOCAL_LEGENDS_VISIBILITY]
            ],
        })
      }
      return null
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_COMMUNITY_FEATURES_FINALIZE,
    status: STRAVA_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: stravaUrls.privacySettingsUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
